import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { AccordionModule } from 'primeng/accordion';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { FormsModule, ReactiveFormsModule, FormArray, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs/operators';
import { AddInstituteService } from '../../../../services/super-admin/add-institute.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Coursedetails, GetCourseResponse } from '../../../../models/Institutedetails/Coursedetails';
import { apiResponse } from '../../../../models/apiResponse';
import { CourseLevelEnum, CourseLevelEnumDescriptions, getCourseLevelEnumFromDescription } from '../../../../models/enums/courseLevelEnum';
import { CheckboxModule } from 'primeng/checkbox';
import { scrollToFirstInvalidControl } from '../../../../direct-apply/shared/validators/form-validation.utils';

@Component({
    selector: 'app-courses',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        AccordionModule,
        DialogModule,
        InputTextModule,
        DropdownModule,
        CalendarModule,
        InputTextareaModule,
        TooltipModule,
        ConfirmDialogModule,
        ToastModule,
        TableModule,
        FormsModule,
        ReactiveFormsModule,
        CheckboxModule
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './courses.component.html',
    styleUrls: ['./courses.component.scss']
})
export class CoursesComponent implements OnInit, OnChanges, OnDestroy {
    @Input() courses!: FormArray;
    @Input() instituteId: string | null = null;
    @Input() isEditMode: boolean = false;
    @Output() onSaveSuccess = new EventEmitter<any>();
    @Output() onSaveError = new EventEmitter<any>();

    showAddCourseDialog: boolean = false;
    courseForm: FormGroup;
    editingIndex: number | null = null;
    newCampusName: string = '';
    showCoursesError: boolean = false;
    savingCourse: boolean = false;

    // FIX 1: Flags to prevent double loadCourses() and suppress
    // the courseNameExists API spam during bulk form population.
    private coursesLoaded = false;
    private isLoadingCourses = false;

    private courseNameCheckSubscription: Subscription | null = null;
    private levelChangeSubscription: Subscription | null = null;

    levels = [
        { label: "Bachelor's Degree", value: CourseLevelEnumDescriptions[CourseLevelEnum.BachelorsDegree] },
        { label: "Master's Degree", value: CourseLevelEnumDescriptions[CourseLevelEnum.MastersDegree] },
        { label: "Doctoral Degree", value: CourseLevelEnumDescriptions[CourseLevelEnum.DoctoralDegree] },
        { label: "Diploma", value: CourseLevelEnumDescriptions[CourseLevelEnum.Diploma] },
        { label: "Certificate", value: CourseLevelEnumDescriptions[CourseLevelEnum.Certificate] }
    ];

    studyModes = [
        { label: 'Full-Time', value: 'Full-Time' },
        { label: 'Part-Time', value: 'Part-Time' },
        { label: 'Online', value: 'Online' },
        { label: 'Hybrid', value: 'Hybrid' }
    ];

    // Values match the backend Currency enum (0=Dollar, 1=Rupee, 2=Euro, 3=Pound).
    currencies = [
        { label: 'USD ($)', value: 0 },
        { label: 'Rupee (₹)', value: 1 },
        { label: 'Euro (€)', value: 2 },
        { label: 'Pound (£)', value: 3 }
    ];

    constructor(
        private fb: FormBuilder,
        private addInstituteService: AddInstituteService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService
    ) {
        const numberPattern = '^[0-9]*$';
        const decimalPattern = /^\d+(\.\d+)?$/;
        const alphanumericPattern = /^[a-zA-Z0-9\s,.-]*$/;
        const alphaPattern = /^[a-zA-Z\s]*$/;
        // Major/Stream is now a comma separated list, so allow commas (and a few common separators).
        const majorPattern = /^[a-zA-Z0-9\s,&.\/-]*$/;
        const urlPattern = /^(https?:\/\/)?[\da-z\.-]+(\.([a-z\.]{2,6}))([\/\w \.-]*)*\/?$/;

        this.courseForm = this.fb.group({
            id: [0],
            name: [''],
            level: ["Bachelor's Degree"],
            major: [''],
            shortForm: [''],
            cipCode: [''],
            studyMode: [''],
            duration: [''],
            startDate: [''],
            location: [''],
            url: [''],
            fees: [''],
            currency: [null],
            oneTimePayment: [''],
            livingExpenses: [''],
            medianEarnings: [''],
            earnings5Years: [''],
            studentExchangePrograms: [false],
            eligibility: [''],
            englishLanguageRequirement: [''],
            courseNames: this.fb.array([])
        });
    }

    ngOnInit() {
        this.setupCourseNameDuplicateCheck();

        const levelControl = this.courseForm.get('level');
        if (levelControl) {
            this.levelChangeSubscription = levelControl.valueChanges
                .subscribe(() => this.checkCourseNameDuplicate());
        }

        // FIX 1a: Only load if not already loaded (ngOnChanges may have triggered first)
        if (this.instituteId && this.isEditMode && !this.coursesLoaded) {
            this.coursesLoaded = true;
            this.loadCourses();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // FIX 1b: Only load once. ngOnChanges fires before ngOnInit,
        // so this flag prevents ngOnInit from loading a second time.
        if (changes['instituteId']?.currentValue && this.isEditMode && !this.coursesLoaded) {
            this.coursesLoaded = true;
            this.loadCourses();
        }
    }

    ngOnDestroy(): void {
        this.courseNameCheckSubscription?.unsubscribe();
        this.courseNameCheckSubscription = null;
        this.levelChangeSubscription?.unsubscribe();
        this.levelChangeSubscription = null;
    }

    // ─── Course Name Duplicate Check ─────────────────────────────────────────
    private setupCourseNameDuplicateCheck(): void {
        const nameControl = this.courseForm.get('name');
        if (!nameControl) return;

        this.courseNameCheckSubscription = nameControl.valueChanges
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                switchMap((name: string) => {
                    // FIX 2: Skip API call entirely while loadCourses() is running.
                    // Without this, every course patched into the FormArray during
                    // bulk load fires a checkCourseNameExists() call — on prod with
                    // 50+ courses that means 50+ simultaneous HTTP requests.
                    if (this.isLoadingCourses) return of(null);

                    const trimmed = (name ?? '').trim();
                    if (trimmed.length < 2) {
                        this.clearCourseNameExistsError();
                        return of(null);
                    }
                    if (!this.instituteId) {
                        this.clearCourseNameExistsError();
                        return of(null);
                    }
                    const excludeId = this.courseForm.get('id')?.value;
                    const excludeCourseId = excludeId && Number(excludeId) > 0 ? Number(excludeId) : undefined;
                    const courseLevel = this.courseForm.get('level')?.value;
                    return this.addInstituteService
                        .checkCourseNameExists(
                            this.instituteId,
                            trimmed,
                            getCourseLevelEnumFromDescription(courseLevel) as CourseLevelEnum,
                            excludeCourseId
                        )
                        .pipe(
                            map((exists) => ({ exists })),
                            catchError(() => of({ exists: false }))
                        );
                })
            )
            .subscribe((result) => {
                if (result == null) return;
                const control = this.courseForm.get('name');
                if (!control) return;
                if (result.exists) {
                    control.setErrors({ ...control.errors, courseNameExists: true });
                } else {
                    this.clearCourseNameExistsError();
                }
            });
    }

    private checkCourseNameDuplicate(): void {
        const nameControl = this.courseForm.get('name');
        if (nameControl) {
            nameControl.updateValueAndValidity({ emitEvent: true });
        }
    }

    private clearCourseNameExistsError(): void {
        const nameControl = this.courseForm.get('name');
        if (!nameControl?.errors) return;
        const err = { ...nameControl.errors };
        delete (err as Record<string, unknown>)['courseNameExists'];
        nameControl.setErrors(Object.keys(err).length > 0 ? err : null);
    }

    // ─── Getters ─────────────────────────────────────────────────────────────
    get courseNames(): FormArray {
        return this.courseForm.get('courseNames') as FormArray;
    }

    // Maps the Currency enum value (0=Dollar,1=Rupee,2=Euro,3=Pound) to a display symbol.
    currencySymbol(currency: number | null | undefined): string {
        switch (currency) {
            case 0: return '$';
            case 1: return '₹';
            case 2: return '€';
            case 3: return '£';
            default: return '';
        }
    }

    addCourseName(name: string) {
        if (name && name.trim()) {
            this.courseNames.push(this.fb.control(name.trim()));
            this.newCampusName = '';
        }
    }

    removeCourseName(index: number) {
        this.courseNames.removeAt(index);
    }

    // ─── Load Courses from API ────────────────────────────────────────────────
    loadCourses() {
        if (!this.instituteId) return;

        // FIX 2: Set flag so valueChanges in the course name field
        // does not fire checkCourseNameExists() for every patched row.
        this.isLoadingCourses = true;

        this.addInstituteService.getcourseDetails({
            instituteId: this.instituteId,
            courseLevel: null,
            searchText: null
        }).subscribe({
            next: (res: any) => {
                let coursesArray: any[] = [];
                if (Array.isArray(res.result)) {
                    coursesArray = res.result;
                } else if (res.result && Array.isArray(res.result.result)) {
                    coursesArray = res.result.result;
                }

                if (coursesArray && coursesArray.length > 0) {
                    this.courses.clear();

                    const numberPattern = '^[0-9]*$';
                    const decimalPattern = /^\d+(\.\d+)?$/;
                    const alphanumericPattern = /^[a-zA-Z0-9\s,.-]*$/;
                    const alphaPattern = /^[a-zA-Z\s]*$/;
                    const majorPattern = /^[a-zA-Z0-9\s,&.\/-]*$/;

                    coursesArray.forEach((course: any) => {
                        this.courses.push(this.fb.group({
                            id: [course.id || 0],
                            name: [course.courseName || ''],
                            level: [course.courseLevel || ''],
                            major: [Array.isArray(course.majorOrStream) ? course.majorOrStream.join(', ') : (course.majorOrStream || '')],
                            shortForm: [course.courseShortForm || ''],
                            cipCode: [course.cipCode?.toString() || ''],
                            studyMode: [course.mode || ''],
                            duration: [course.duration != null ? String(course.duration) : ''],
                            startDate: [course.startDate ? new Date(course.startDate) : null],
                            location: [course.courseLocation || ''],
                            url: [course.courseUrl || ''],
                            fees: [course.courseFees != null ? String(course.courseFees) : ''],
                            currency: [course.currency ?? null],
                            oneTimePayment: [course.oneTimePayment || ''],
                            livingExpenses: [course.hostelAndLivingExpenses || ''],
                            medianEarnings: [course.medianEarningPostGraduation || ''],
                            earnings5Years: [course.workingGraduatesMedianEarningsNotEnrolledPost5YearsCompleting || ''],
                            studentExchangePrograms: [!!course.hasStudentExchange],
                            eligibility: [course.eligibilityRequirement || ''],
                            englishLanguageRequirement: [course.englishLanguageRequirement || ''],
                            courseNames: this.fb.array(
                                (course.courses || []).map((name: string) => this.fb.control(name))
                            )
                        }));
                    });
                }

                // FIX 2: Done bulk-loading; re-enable the duplicate name check.
                this.isLoadingCourses = false;
            },
            error: () => {
                this.isLoadingCourses = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load courses'
                });
            }
        });
    }

    // ─── Map form value → API request ────────────────────────────────────────
    private mapFormToRequest(formValue: any): Coursedetails {
        return {
            instituteId: String(this.instituteId || ''),
            courseName: formValue.name || '',
            courseLevel: formValue.level || '',
            majorOrStream: (formValue.major || '')
                .split(',')
                .map((s: string) => s.trim())
                .filter((s: string) => s.length > 0),
            courseShortForm: formValue.shortForm || '',
            cipCode: parseInt(formValue.cipCode) || 0,
            mode: formValue.studyMode || '',
            duration: formValue.duration !== '' && formValue.duration != null ? String(formValue.duration) : null,
            startDate: formValue.startDate && !isNaN(new Date(formValue.startDate).getTime())
                ? new Date(formValue.startDate).toISOString()
                : '',
            courseLocation: formValue.location || '',
            courseUrl: formValue.url || '',
            courseFees: formValue.fees !== '' && formValue.fees != null ? String(formValue.fees) : null,
            currency: formValue.currency ?? null,
            oneTimePayment: formValue.oneTimePayment || '',
            hostelAndLivingExpenses: formValue.livingExpenses || '',
            medianEarningPostGraduation: formValue.medianEarnings || '',
            workingGraduatesMedianEarningsNotEnrolledPost5YearsCompleting: formValue.earnings5Years || '',
            hasStudentExchange: !!formValue.studentExchangePrograms,
            eligibilityRequirement: formValue.eligibility || '',
            englishLanguageRequirement: formValue.englishLanguageRequirement || '',
            totalNumberOfCourseOffered: String(this.courses.length + (this.editingIndex === null ? 1 : 0)),
            courses: formValue.courseNames || [],
            id: formValue.id > 0 ? formValue.id : undefined
        };
    }

    // ─── Dialog Open/Close ────────────────────────────────────────────────────
    openAddCourseDialog() {
        this.resetForm();
        this.editingIndex = null;
        this.showAddCourseDialog = true;
    }

    closeAddCourseDialog() {
        this.showAddCourseDialog = false;
        this.editingIndex = null;
    }

    private resetForm() {
        this.courseForm.reset({
            id: 0,
            level: "Bachelor's Degree",
            name: '',
            major: '',
            shortForm: '',
            cipCode: '',
            studyMode: '',
            duration: '',
            startDate: null,
            location: '',
            url: '',
            fees: '',
            currency: null,
            oneTimePayment: '',
            livingExpenses: '',
            medianEarnings: '',
            earnings5Years: '',
            studentExchangePrograms: false,
            eligibility: '',
            englishLanguageRequirement: ''
        });
        while (this.courseNames.length) {
            this.courseNames.removeAt(0);
        }
        this.newCampusName = '';
    }

    // ─── Add / Update Course ──────────────────────────────────────────────────
    addCourse() {
        if (!this.instituteId) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Institute ID is missing. Please save Basic Information first.'
            });
            return;
        }

        if (!this.courseForm.valid) {
            this.markFormGroupTouched(this.courseForm);
            scrollToFirstInvalidControl(true);
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please fill in all required fields correctly.'
            });
            return;
        }

        const formValue = this.courseForm.value;
        const request = this.mapFormToRequest(formValue);
        const isUpdate = formValue.id && formValue.id > 0;

        const apiCall = isUpdate
            ? this.addInstituteService.updateCourseDetails(request, String(formValue.id))
            : this.addInstituteService.courseDetails(request);

        this.savingCourse = true;
        apiCall.subscribe({
            next: (res: any) => {
                this.savingCourse = false;
                    console.log('API response after save:', JSON.stringify(res));
                const isEditing = this.editingIndex !== null;

                // FIX 3: Update the FormArray locally instead of calling loadCourses().
                // Previously, every save triggered a full API reload + clear() + re-push
                // of ALL courses, causing heavy JS work and N more API calls on prod.
                const numberPattern = '^[0-9]*$';
                const alphanumericPattern = /^[a-zA-Z0-9\s,.-]*$/;
                const alphaPattern = /^[a-zA-Z\s]*$/;
                const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

                // Build a proper FormGroup for the saved course.
                // Use the id returned by the API for newly created courses.
                const savedId = res?.id ||  res?.result?.id || res?.result?.courseId || formValue.id || 0;
                const savedGroup = this.fb.group({
                    id: [savedId],
                    name: [formValue.name || ''],
                    level: [formValue.level || ''],
                    major: [formValue.major || ''],
                    shortForm: [formValue.shortForm || ''],
                    cipCode: [formValue.cipCode || ''],
                    studyMode: [formValue.studyMode || ''],
                    duration: [formValue.duration || ''],
                    startDate: [formValue.startDate || null],
                    location: [formValue.location || ''],
                    url: [formValue.url || ''],
                    fees: [formValue.fees || ''],
                    oneTimePayment: [formValue.oneTimePayment || ''],
                    livingExpenses: [formValue.livingExpenses || ''],
                    medianEarnings: [formValue.medianEarnings || ''],
                    earnings5Years: [formValue.earnings5Years || ''],
                    studentExchangePrograms: [!!formValue.studentExchangePrograms],
                    eligibility: [formValue.eligibility || ''],
                    englishLanguageRequirement: [formValue.englishLanguageRequirement || ''],
                    courseNames: this.fb.array(
                        (formValue.courseNames || []).map((n: string) => this.fb.control(n))
                    )
                });

                if (isEditing && this.editingIndex !== null) {
                    // Replace the existing FormGroup at that index.
                    this.courses.setControl(this.editingIndex, savedGroup);
                } else {
                    // Append the new course.
                    this.courses.push(savedGroup);
                }

                this.messageService.add({
                    severity: 'success',
                    summary: isEditing ? 'Updated' : 'Added',
                    detail: isEditing ? 'Course updated successfully' : 'Course added successfully'
                });

                this.resetForm();
                this.showAddCourseDialog = false;
                this.editingIndex = null;
            },
            error: (err: any) => {
                this.savingCourse = false;
                const message = err?.message ?? 'Failed to save course details';
                const isDuplicateCourse = typeof message === 'string' &&
                    /course.*already exists|already exists.*course/i.test(message);

                if (isDuplicateCourse) {
                    const nameControl = this.courseForm.get('name');
                    if (nameControl) {
                        nameControl.setErrors({ ...nameControl.errors, courseNameExists: true });
                    }
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Duplicate Course',
                        detail: message
                    });
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: message
                    });
                }
            }
        });
    }

    // ─── Edit Course ──────────────────────────────────────────────────────────
    editCourse(index: number) {
    this.editingIndex = index;
    const course = this.courses.at(index).value;
        console.log('Editing course id:', course.id);
    this.courseForm.patchValue({ id: course.id });
    this.courseForm.patchValue(course);

    while (this.courseNames.length) {
        this.courseNames.removeAt(0);
    }
    if (course.courseNames && Array.isArray(course.courseNames)) {
        course.courseNames.forEach((name: string) => {
            this.courseNames.push(this.fb.control(name));
        });
    }

    this.showAddCourseDialog = true;
}

    // ─── Delete Course ────────────────────────────────────────────────────────
    removeCourse(index: number) {
        const course = this.courses.at(index).value;
        const courseId = course.id || course.Id || course.courseId;

        this.confirmationService.confirm({
            message: 'Are you sure you want to delete this course?',
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                if (courseId && courseId > 0) {
                    this.addInstituteService.deleteCourseDetails(String(courseId)).subscribe({
                        next: () => {
                            this.courses.removeAt(index);
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Deleted',
                                detail: 'Course deleted successfully'
                            });
                        },
                        error: () => {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Failed to delete course from server'
                            });
                        }
                    });
                } else {
                    this.courses.removeAt(index);
                    this.messageService.add({
                        severity: 'info',
                        summary: 'Removed',
                        detail: 'Unsaved course removed'
                    });
                }
            }
        });
    }

    // ─── Save (emit to parent) ────────────────────────────────────────────────
    save() {
        this.showCoursesError = false;
        this.onSaveSuccess.emit({
            mode: this.isEditMode ? 'edit' : 'add',
            response: { result: { id: this.instituteId } }
        });
    }

    // ─── Helpers ──────────────────────────────────────────────────────────────
    private markFormGroupTouched(group: FormGroup | FormArray) {
        group.markAsTouched();
        Object.values(group.controls).forEach(control => {
            if (control instanceof FormGroup || control instanceof FormArray) {
                this.markFormGroupTouched(control);
            } else {
                control.markAsTouched();
                control.markAsDirty(); // Trigger UI validation state
            }
        });
    }
}
